import {
  Globe2,
  Loader2,
  Maximize2,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Radio,
} from "lucide-react";
import { Room, RoomEvent, createLocalAudioTrack, type LocalAudioTrack } from "livekit-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLiveCall } from "../../context/LiveCallContext";
import { core } from "../../lib/api";
import { splitBackgroundAudioOptions } from "../../lib/backgroundAudio";
import { DEFAULT_CAMPAIGN_NAME, normalizeCampaignName } from "../../lib/campaignOptions";
import { StepContacts } from "../batches/wizard/steps";
import type { WizardState } from "../batches/wizard/create-batch-wizard";
import { getAgentScheduleSettings, getScheduleSettings } from "../../lib/userAccess";
import { cn } from "../../lib/utils";
import { Dropdown } from "../../components/ui/dropdown";
import { MultiSelectAudioDropdown, type SelectedAudio } from "../../components/ui/multi-select-audio-dropdown";
import { ToggleSwitch } from "../../components/ui/toggle-switch";
import { useToast } from "../../components/ui/toast";
import { Modal } from "../../components/ui/modal";
import { phoneNumberFromRow } from "../../lib/phone-number";
import type { StudioVersion } from "./studio-editors";
import { readCut } from "../supervision/lib/speech-cut";
import type { TranscriptMessage as SupervisedTranscriptMessage } from "../supervision/types";

type TestChannel = "web" | "telephony";

interface VoiceOption {
  local_voice_name?: string;
  gender?: string;
}

interface DidOption {
  id?: string;
  number?: string;
}

interface BackgroundOption {
  id?: string;
  name: string;
  volume?: number;
  type?: string;
  file_key?: string;
}

interface TestWorkspaceProps {
  agentId: string;
  agentName: string;
  architecture: "single" | "thinker_talker";
  activeVersionId: string;
  versions: StudioVersion[];
  isNew: boolean;
  dirty: boolean;
  ensureVersions: () => Promise<StudioVersion[]>;
  loadVersionDetails: (versionId: string) => Promise<StudioVersion>;
  webOnly?: boolean;
  dirtyReason?: string;
  allowTranscriptExpand?: boolean;
}

/**
 * A live-transcript turn. Structural rather than the supervision feature's own
 * type: this panel has always been looser about the payload, and the shared
 * message type carries required fields this stream does not guarantee.
 */
type TranscriptMessage = Partial<SupervisedTranscriptMessage> & {
  index?: number;
  role?: string;
  text?: string;
};

function voiceName(option: VoiceOption) {
  return String(option.local_voice_name || "").trim();
}

function randomContactId() {
  return `cont-${crypto.randomUUID().replace(/-/g, "").slice(0, 8).toUpperCase()}`;
}

function customTts(voice: string) {
  return ["sia", "meera"].includes(voice.trim().toLowerCase());
}

function transcriptTime(value?: string) {
  const date = new Date(value || "");
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function TranscriptMessages({
  messages,
  loading,
  belongsHere,
  emptyClassName,
}: {
  messages: TranscriptMessage[];
  loading: boolean;
  belongsHere: boolean;
  emptyClassName: string;
}) {
  if (loading && belongsHere && messages.length === 0) {
    return (
      <div className={cn("flex items-center justify-center text-xs text-ink-3", emptyClassName)}>
        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Connecting transcript
      </div>
    );
  }
  if (!belongsHere || messages.length === 0) {
    return (
      <div className={cn("flex items-center justify-center text-center text-xs leading-5 text-ink-3", emptyClassName)}>
        Transcript lines will appear here while the selected test is running.
      </div>
    );
  }
  return (
    <>
      {messages.map((message, index) => {
        const assistant = message.role === "assistant";
        const cut = readCut(message);
        // Only the spoken part. A turn cut before any audio stays empty rather
        // than falling back to the full generated sentence, which is the one
        // thing that must never be shown as the agent's speech.
        const raw = cut.visible ? cut.spoken : message.text || "";
        const text = raw.replace(/^"|"$/g, "");
        return (
          <div
            key={`${message.item_id || message.index || message.created_at || index}-${message.text || ""}`}
            className={cn(
              "max-w-[90%] rounded-md border px-3 py-2",
              assistant ? "mr-auto border-line bg-elevated" : "ml-auto border-ink/15 bg-ink text-canvas",
            )}
          >
            <div className="mb-1 flex justify-between gap-3">
              <span className={cn("font-mono text-[9px] font-bold uppercase", assistant ? "text-ink-3" : "text-canvas/60")}>{assistant ? "Agent" : "Caller"}</span>
              <span className={cn("font-mono text-[9px]", assistant ? "text-ink-3" : "text-canvas/60")}>{transcriptTime(message.created_at)}</span>
            </div>
            <p className="whitespace-pre-wrap text-xs leading-5">{text}</p>
          </div>
        );
      })}
    </>
  );
}

export function AgentTestWorkspace({
  agentId,
  agentName,
  architecture,
  activeVersionId,
  versions,
  isNew,
  dirty,
  ensureVersions,
  loadVersionDetails,
  webOnly = false,
  dirtyReason = "Publish or discard local changes before testing.",
  allowTranscriptExpand = false,
}: TestWorkspaceProps) {
  const { token, user } = useAuth();
  const toast = useToast();
  const {
    liveTranscriptTarget,
    liveTranscript,
    liveTranscriptLoading,
    startLiveTranscript,
    updateLiveTranscriptTarget,
    closeLiveTranscript,
  } = useLiveCall();
  const [channel, setChannel] = useState<TestChannel>(webOnly ? "web" : "telephony");
  const [selectedVersionId, setSelectedVersionId] = useState(activeVersionId);
  
  useEffect(() => {
    if (activeVersionId) {
      setSelectedVersionId(activeVersionId);
    }
  }, [activeVersionId]);
  const [selectedVersion, setSelectedVersion] = useState<StudioVersion | null>(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [voiceOptions, setVoiceOptions] = useState<VoiceOption[]>([]);
  const [didOptions, setDidOptions] = useState<DidOption[]>([]);
  const [backgroundOptions, setBackgroundOptions] = useState<{
    ambientSoundOptions: BackgroundOption[];
    thinkingSoundOptions: BackgroundOption[];
  }>({ ambientSoundOptions: [], thinkingSoundOptions: [] });
  const [voice, setVoice] = useState("");
  const [didId, setDidId] = useState("");
  const [interruption, setInterruption] = useState(false);
  const [backgroundEnabled, setBackgroundEnabled] = useState(false);
  const [ambientSound, setAmbientSound] = useState<SelectedAudio[]>([]);
  const [thinkingSound, setThinkingSound] = useState<SelectedAudio[]>([]);
  const [status, setStatus] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [webCallActive, setWebCallActive] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const roomRef = useRef<Room | null>(null);
  const micRef = useRef<LocalAudioTrack | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const callIdRef = useRef("");
  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  const handleScroll = useCallback(() => {
    const el = transcriptContainerRef.current;
    if (!el) return;
    // 10px tolerance for "at bottom"
    isAtBottomRef.current = Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) < 10;
  }, []);

  useEffect(() => {
    if (isAtBottomRef.current && transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [liveTranscript?.messages]);

  const scheduleSettings = getScheduleSettings(user);
  const runtimeSettings = getAgentScheduleSettings(user, agentId, "");
  const canTest = Boolean(agentId && selectedVersionId && !isNew && !dirty);
  const availableVersions = versions.length
    ? versions
    : selectedVersion
      ? [selectedVersion]
      : [];

  const [s, setS] = useState<WizardState>({
    agentId, version: activeVersionId, voiceId: "", did: "", campaign: "", langRouting: true, fallback: "end",
    interruption: true, bgNoise: true, ambience: [], backgroundAudio: [],
    dltId: "", header: "", dndScrub: true, windows: ["08-12", "12-17", "17-21"],
    contactSource: "manual", fileName: null, fileSize: null, rawRows: [], csvHeaders: [], mapping: {}, excludedRowIndices: [],
    normalized: [], rejectedContacts: [], rowErrors: [], deduped: 0, dndRemoved: 0, manualRows: [{}],
    mode: "now", at: "", timezone: "Asia/Kolkata", concurrency: 50, budget: "",
    useAlternateNumber: false, multiAgentConfigId: "",
    isRetryLocked: false,
    step: 0,
  });

  const patch = useCallback((next: Partial<WizardState>) => setS((current) => ({ ...current, ...next })), []);

  const messages = Array.isArray(liveTranscript?.messages)
    ? liveTranscript.messages as TranscriptMessage[]
    : [];
  const transcriptBelongsHere = liveTranscriptTarget?.inline === true
    && (!liveTranscriptTarget.agent_id || liveTranscriptTarget.agent_id === agentId);

  useEffect(() => {
    setSelectedVersionId((current) => current || activeVersionId);
  }, [activeVersionId]);

  useEffect(() => {
    if (webOnly) setChannel("web");
  }, [webOnly]);

  useEffect(() => {
    if (isNew) return undefined;
    const timer = window.setTimeout(() => {
      setLoadingOptions(true);
      void Promise.all([
        ensureVersions(),
        core<{ voices?: VoiceOption[] }>("/api/v1/voice/options?active_only=true"),
        webOnly
          ? Promise.resolve({ did_numbers: [] as DidOption[] })
          : core<{ did_numbers?: DidOption[] }>("/api/v1/voice/did-options?active_only=true"),
        scheduleSettings.background_noise
          ? core<{ background_voices?: BackgroundOption[] }>("/api/v1/voice/background-options")
          : Promise.resolve({ background_voices: [] }),
      ]).then(([, voicesResult, didResult, backgroundResult]) => {
        setVoiceOptions((voicesResult.voices || []).filter((item) => voiceName(item)));
        setDidOptions((didResult.did_numbers || []).filter((item) => item.id && item.number));
        setBackgroundOptions(splitBackgroundAudioOptions(backgroundResult.background_voices || []));
      }).catch((error) => {
        toast.danger("Test controls unavailable", (error as Error).message);
      }).finally(() => setLoadingOptions(false));
    }, 150);
    return () => window.clearTimeout(timer);
  }, [ensureVersions, isNew, scheduleSettings.background_noise, toast, webOnly]);

  useEffect(() => {
    if (!selectedVersionId) {
      setSelectedVersion(null);
      return;
    }
    const row = versions.find((version) => version.version_id === selectedVersionId);
    if (row && row.is_summary !== true) {
      setSelectedVersion(row);
      return;
    }
    let cancelled = false;
    setLoadingVersion(true);
    loadVersionDetails(selectedVersionId)
      .then((detail) => {
        if (!cancelled) setSelectedVersion(detail);
      })
      .catch((error) => {
        if (!cancelled) toast.danger("Version could not be loaded", (error as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoadingVersion(false);
      });
    return () => { cancelled = true; };
  }, [loadVersionDetails, selectedVersionId, toast, versions]);

  useEffect(() => {
    const audio = document.createElement("audio");
    audio.autoplay = true;
    document.body.appendChild(audio);
    audioRef.current = audio;
    return () => {
      if (roomRef.current) void roomRef.current.disconnect();
      micRef.current?.stop();
      audio.remove();
      audioRef.current = null;
    };
  }, []);

  const backgroundPayload = {
    background_noise: scheduleSettings.background_noise && backgroundEnabled,
    ambience: scheduleSettings.background_noise && backgroundEnabled ? ambientSound : [],
    background_audio: scheduleSettings.background_noise && backgroundEnabled ? thinkingSound : [],
  };

  const runtimePayload = {
    interruption: scheduleSettings.interruption_enabled ? interruption : false,
    call_retry_enabled: runtimeSettings.call_retry_enabled,
    short_call_retry_enabled: runtimeSettings.short_call_retry_enabled,
    llm_skip_enabled: runtimeSettings.llm_skip_enabled,
    llm_skip_seconds: runtimeSettings.llm_skip_seconds,
    call_retry_attempts: runtimeSettings.call_retry_attempts,
    call_retry_delay_minutes: runtimeSettings.call_retry_delay_minutes,
    short_call_retry_seconds: runtimeSettings.short_call_retry_seconds,
    auto_disconnect_timeout_seconds: runtimeSettings.auto_disconnect_timeout_seconds,
    auto_disconnect_message: runtimeSettings.auto_disconnect_message,
    user_away_timeout_seconds: runtimeSettings.user_away_timeout_seconds,
    away_first_message: runtimeSettings.away_first_message,
    away_disconnect_countdown_seconds: runtimeSettings.away_disconnect_countdown_seconds,
    away_disconnect_message: runtimeSettings.away_disconnect_message,
  };

  const updateStatus = useCallback((next: string, muted = micMuted) => {
    setStatus(next);
    setMicMuted(muted);
    updateLiveTranscriptTarget({ callStatus: next, isMicMuted: muted });
  }, [micMuted, updateLiveTranscriptTarget]);

  const endWebCall = useCallback(async () => {
    const callId = callIdRef.current;
    updateStatus("Ending web call...");
    if (callId) {
      void core(`/api/v1/test-call/${encodeURIComponent(callId)}/hangup`, {
        method: "POST",
        body: JSON.stringify({}),
      }).catch(() => undefined);
    }
    micRef.current?.stop();
    micRef.current = null;
    if (roomRef.current) {
      await roomRef.current.disconnect().catch(() => undefined);
      roomRef.current = null;
    }
    callIdRef.current = "";
    setWebCallActive(false);
    setConnecting(false);
    setMicMuted(false);
    setStatus("Web call ended");
    closeLiveTranscript();
  }, [closeLiveTranscript, updateStatus]);

  const toggleMute = useCallback(async () => {
    const track = micRef.current;
    if (!track) return;
    const next = !micMuted;
    if (next) await track.mute();
    else await track.unmute();
    updateStatus(next ? "Microphone muted" : "Microphone active", next);
  }, [micMuted, updateStatus]);

  const startWebCall = async () => {
    if (!canTest || !selectedVersion) {
      toast.warning("Publish before testing", dirty ? dirtyReason : "Select a published version.");
      return;
    }
    if (!voice) {
      toast.warning("Voice required", "Select a voice before starting the web call.");
      return;
    }

    const firstRow = s.manualRows?.[0] || {};
    const contactId = randomContactId();
    const participantName = String(firstRow.name || user?.name || user?.email || "Agent Tester").trim();
    const customerPhone = phoneNumberFromRow(firstRow, String(user?.phone || "9999999999"));
    const selectedVoice = voiceOptions.find((item) => voiceName(item).toLowerCase() === voice.toLowerCase());
    
    // Remove known fields to pass the rest as infields
    const { name, phone_number, phone_number_cc, customer_phone, contact_id, phone, ...restInfields } = firstRow;

    const contactMetadata = {
      contact_id: contactId,
      name: participantName,
      customer_phone: customerPhone,
      phone_number: customerPhone,
    };
    
    try {
      setConnecting(true);
      setStatus("Starting web call...");
      const result = await core<{
        token: string;
        ws_url: string;
        room_name: string;
        call_id: string;
        contact_id?: string;
        participant_identity: string;
      }>("/api/v1/test-call/token", {
        method: "POST",
        body: JSON.stringify({
          type: "web_call",
          agent_id: agentId,
          agent_name: agentName,
          version_id: selectedVersionId,
          version_name: selectedVersion.version_name || selectedVersionId,
          client_id: user?.id || "",
          contact_id: contactId,
          participant_name: participantName,
          voice,
          gender: selectedVoice?.gender || null,
          custom_tts: customTts(voice),
          ...runtimePayload,
          ...backgroundPayload,
          campaign: normalizeCampaignName(DEFAULT_CAMPAIGN_NAME),
          infieldData: restInfields,
          contactMetadata,
          multi_agent_config_id: null,
          thinker_and_talker: architecture === "thinker_talker",
        }),
      });
      const room = new Room();
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === "audio" && audioRef.current) {
          track.attach(audioRef.current);
          void audioRef.current.play();
        }
      });
      room.on(RoomEvent.Disconnected, () => {
        roomRef.current = null;
        micRef.current = null;
        callIdRef.current = "";
        setWebCallActive(false);
        setConnecting(false);
        setStatus("Web call disconnected");
      });
      await room.connect(result.ws_url, result.token);
      const mic = await createLocalAudioTrack();
      await room.localParticipant.publishTrack(mic);
      roomRef.current = room;
      micRef.current = mic;
      callIdRef.current = result.call_id;
      setWebCallActive(true);
      setConnecting(false);
      setStatus("Connected");
      startLiveTranscript({
        inline: true,
        agent_id: agentId,
        call_id: result.call_id,
        contact_id: result.contact_id || contactId,
        room_name: result.room_name,
        participant_identity: result.participant_identity,
        agent_name: agentName,
        version_name: selectedVersion.version_name || selectedVersionId,
        contact_name: participantName,
        callStatus: "Connected",
        isMicMuted: false,
        onToggleMute: toggleMute,
        onHangup: endWebCall,
      });
    } catch (error) {
      setConnecting(false);
      setWebCallActive(false);
      setStatus("Web call failed");
      toast.danger("Web call failed", (error as Error).message);
    }
  };

  const startTelephonyCall = async () => {
    if (!canTest || !selectedVersion) {
      toast.warning("Publish before testing", dirty ? "Publish or discard local changes first." : "Select a published version.");
      return;
    }
    if (!voice || !didId) {
      toast.warning("Call details required", "Select a voice and DID.");
      return;
    }
    const firstRow = s.manualRows?.[0] || {};
    const fullPhone = phoneNumberFromRow(firstRow, "9999999999");
    
    if (fullPhone.replace(/\D/g, "").length < 7) {
      toast.warning("Invalid phone", "The first contact row must have a valid phone number.");
      return;
    }
    
    if (!token) {
      toast.warning("Authentication required", "Sign in again before starting a test call.");
      return;
    }
    
    const selectedVoice = voiceOptions.find((item) => voiceName(item).toLowerCase() === voice.toLowerCase());
    const contactId = randomContactId();
    const contactName = String(firstRow.name || fullPhone);
    const { name, phone_number, phone_number_cc, customer_phone, contact_id, phone, ...restInfields } = firstRow;

    const contactMetadata = {
      contact_id: contactId,
      customer_phone: fullPhone,
      phone_number: fullPhone,
      name: contactName,
    };

    try {
      setConnecting(true);
      setStatus("Queuing live call...");
      const result = await core<{ call_id?: string; message?: string }>("/api/v1/test-call/telephony", {
        method: "POST",
        body: JSON.stringify({
          type: "telephony_call",
          agent_id: agentId,
          agent_name: agentName,
          version_id: selectedVersionId,
          version_name: selectedVersion.version_name || selectedVersionId,
          client_id: user?.id || "",
          contact_id: contactId,
          customer_phone: fullPhone,
          phone_number: fullPhone,
          voice,
          gender: selectedVoice?.gender || null,
          custom_tts: customTts(voice),
          ...runtimePayload,
          ...backgroundPayload,
          did_number_id: didId,
          campaign: normalizeCampaignName(DEFAULT_CAMPAIGN_NAME),
          infield_data: restInfields,
          contact_metadata: contactMetadata,
          multi_agent_config_id: null,
          thinker_and_talker: architecture === "thinker_talker",
        }),
      });
      setConnecting(false);
      setStatus("Live call queued");
      if (result.call_id) {
        startLiveTranscript({
          inline: true,
          agent_id: agentId,
          call_id: result.call_id,
          contact_name: contactName,
          customer_name: contactName,
          customer_phone: fullPhone,
          callStatus: "Live call queued",
        });
      }
      toast.success("Live call initiated", result.message || fullPhone);
    } catch (error) {
      setConnecting(false);
      setStatus("Live call failed");
      toast.danger("Live call failed", (error as Error).message);
    }
  };

  return (
    <div className={cn(
      "flex flex-col overflow-hidden rounded-md border border-line bg-elevated shadow-sm",
      webOnly ? "min-h-[440px]" : "min-h-[640px]",
    )}>
      <div className="flex h-12 shrink-0 items-center border-b border-line px-3">
        {webOnly ? (
          <span className="flex h-8 items-center gap-2 px-1 text-xs font-bold text-ink">
            <Globe2 className="h-3.5 w-3.5" /> Web call
          </span>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setChannel("telephony")}
              className={cn("flex h-8 items-center gap-2 rounded-md px-3 text-xs font-bold", channel === "telephony" ? "bg-ink text-canvas" : "text-ink-3 hover:bg-soft hover:text-ink")}
            >
              <Phone className="h-3.5 w-3.5" /> Live call
            </button>
            <button
              type="button"
              onClick={() => setChannel("web")}
              className={cn("flex h-8 items-center gap-2 rounded-md px-3 text-xs font-bold", channel === "web" ? "bg-ink text-canvas" : "text-ink-3 hover:bg-soft hover:text-ink")}
            >
              <Globe2 className="h-3.5 w-3.5" /> Web call
            </button>
          </>
        )}
        {loadingOptions && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-ink-3" />}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className={cn("space-y-4 border-b border-line", webOnly ? "p-3" : "p-4")}>
          <div>
            <span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Published version</span>
            <Dropdown
              ariaLabel="Select published version"
              value={selectedVersionId}
              onChange={setSelectedVersionId}
              options={
                availableVersions.length
                  ? availableVersions.map((version) => ({
                      value: version.version_id,
                      label: version.version_name || version.version_id,
                    }))
                  : [{ value: activeVersionId, label: activeVersionId || "No published version" }]
              }
            />
          </div>
          <div>
            <span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Voice</span>
            <Dropdown
              ariaLabel="Select voice"
              value={voice}
              onChange={setVoice}
              options={[
                { value: "", label: "Select voice" },
                ...voiceOptions.map((option) => {
                  const name = voiceName(option);
                  return {
                    value: name,
                    label: `${name}${option.gender ? ` (${option.gender})` : ""}`,
                  };
                })
              ]}
            />
          </div>
          {scheduleSettings.interruption_enabled && (
            <div className="flex items-center justify-between rounded-md border border-line bg-soft px-3 py-2.5 text-xs font-bold text-ink">
              Allow interruption
              <ToggleSwitch checked={interruption} onChange={setInterruption} ariaLabel="Allow interruption" />
            </div>
          )}
          {scheduleSettings.background_noise && (
            <div className="space-y-3 rounded-md border border-line bg-soft p-3">
              <div className="flex items-center justify-between text-xs font-bold text-ink">
                Background audio
                <ToggleSwitch checked={backgroundEnabled} onChange={setBackgroundEnabled} ariaLabel="Background audio" />
              </div>
              {backgroundEnabled && (
                <div className="grid gap-2">
                  <MultiSelectAudioDropdown
                    label="Ambience"
                    placeholder="Select ambience..."
                    options={backgroundOptions.ambientSoundOptions.map(b => ({ ...b, id: b.id || b.file_key || b.name }))}
                    value={ambientSound}
                    onChange={setAmbientSound}
                  />
                  <MultiSelectAudioDropdown
                    label="Thinking sound"
                    placeholder="Select thinking sound..."
                    options={backgroundOptions.thinkingSoundOptions.map(b => ({ ...b, id: b.id || b.file_key || b.name }))}
                    value={thinkingSound}
                    onChange={setThinkingSound}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-b border-line bg-transparent">
           <StepContacts s={s} patch={patch} hideValidate={true} />
           <p className="mt-4 text-[11px] text-ink-3 text-center">
             Only the first contact row will be used for the test call.
           </p>
        </div>

        {webOnly || channel === "web" ? (
          <div className={cn("space-y-4", webOnly ? "p-3" : "p-4")}>
            <div className="rounded-md border border-line bg-soft p-4">
              <p className="text-xs font-bold text-ink">Browser microphone call</p>
              <p className="mt-1 text-[11px] leading-5 text-ink-3">Tests using your browser's microphone.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={!canTest || !voice || connecting || webCallActive || loadingVersion} onClick={() => void startWebCall()} className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md bg-ink px-3 text-xs font-bold text-canvas hover:opacity-90 disabled:cursor-not-allowed disabled:bg-line disabled:text-ink-3">
                {connecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
                {connecting ? "Connecting" : webCallActive ? "Call active" : "Start audio test"}
              </button>
              {webCallActive && (
                <>
                  <button type="button" onClick={() => void toggleMute()} className="flex h-9 w-9 items-center justify-center rounded-md border border-line bg-elevated text-ink hover:bg-soft" title={micMuted ? "Unmute" : "Mute"}>
                    {micMuted ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => void endWebCall()} className="flex h-9 w-9 items-center justify-center rounded-md border border-danger/20 bg-danger-soft text-danger" title="Hang up">
                    <PhoneOff className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4 p-4">
            <Dropdown
              ariaLabel="Select DID number"
              value={didId}
              onChange={setDidId}
              options={[
                { value: "", label: "Select DID number" },
                ...didOptions.map((option) => ({
                  value: option.id || "",
                  label: option.number || "",
                }))
              ]}
            />
            <button type="button" disabled={!canTest || !voice || !didId || connecting || loadingVersion || !s.manualRows[0]?.phone_number} onClick={() => void startTelephonyCall()} className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-ink px-3 text-xs font-bold text-canvas hover:opacity-90 disabled:cursor-not-allowed disabled:bg-line disabled:text-ink-3">
              {connecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Phone className="h-3.5 w-3.5" />}
              {connecting ? "Starting call" : "Start live call"}
            </button>
          </div>
        )}

        <div className="border-t border-line bg-canvas">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="inline-flex items-center gap-2 text-xs font-bold text-ink">
              <Radio className={cn("h-3.5 w-3.5", transcriptBelongsHere ? "text-success" : "text-ink-3")} />
              Live transcript
            </span>
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate font-mono text-[10px] text-ink-3">{status || "Idle"}</span>
              {allowTranscriptExpand && (
                <button
                  type="button"
                  onClick={() => setTranscriptExpanded(true)}
                  aria-label="Enlarge transcript"
                  title="Enlarge transcript"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-line bg-elevated text-ink-3 transition-colors hover:border-brand hover:text-ink"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
          <div
            ref={transcriptContainerRef}
            onScroll={handleScroll}
            className={cn(
            "space-y-2 overflow-y-auto border-t border-line p-3",
            webOnly ? "max-h-48 min-h-24" : "max-h-72 min-h-40",
          )}>
            <TranscriptMessages
              messages={messages}
              loading={liveTranscriptLoading}
              belongsHere={transcriptBelongsHere}
              emptyClassName={webOnly ? "min-h-20" : "min-h-32"}
            />
          </div>
        </div>
      </div>
      {(isNew || dirty) && (
        <div className="border-t border-warning/20 bg-warning-soft px-4 py-3 text-[11px] font-semibold text-warning">
          {isNew ? "Create the agent before testing." : dirtyReason}
        </div>
      )}
      {allowTranscriptExpand && transcriptExpanded && (
        <Modal
          title="Live transcript"
          subtitle={`${agentName} · ${status || "Idle"}`}
          onClose={() => setTranscriptExpanded(false)}
          width="max-w-5xl"
          maxHeight="max-h-[88vh]"
        >
          <div className="min-h-[55vh] space-y-3 overflow-y-auto rounded-lg border border-line bg-canvas p-4">
            <TranscriptMessages
              messages={messages}
              loading={liveTranscriptLoading}
              belongsHere={transcriptBelongsHere}
              emptyClassName="min-h-[50vh]"
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
